/**
 * BG progress carousel V2 — autoplay, progress tabs, PC hover-pause, MO arrows + swipe.
 *
 * Theme-block architecture (same constraint as slideshow-v2):
 * - Slides render via Liquid `{% content_for 'blocks' %}`.
 * - `section.blocks` Liquid count is often 0, so tabs cannot be Liquid-SSR'd
 *   the way v1 does. Tabs are built from slide DOM (`data-bg-progress-v2-label`).
 *
 * MO interactions that advance index by one (media swipe / tab swipe / arrows)
 * all share `_stepBy`. Slide change mirrors slideshow-v2 `content_fade`:
 * 300ms host crossfade, then the copy block fades in as a unit (no stagger).
 */
class BgProgressCarouselV2 extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.paused = false;
    this.elapsedBeforePause = 0;
    this.progressStart = 0;
    this.progressRaf = null;
    this.isInView = false;
    this.intersectionObserver = null;
    /** @type {HTMLElement[]} */
    this.slides = [];
    /** @type {HTMLButtonElement[]} */
    this.tabs = [];
    /** @type {HTMLElement[]} */
    this._swipeTargets = [];
    /** @type {{ startX: number, startY: number, tracking: boolean, axis: 'x' | 'y' | null, moved: boolean } | null} */
    this._swipeState = null;
    this._suppressTabClick = false;

    this._onTabClick = this._onTabClick.bind(this);
    this._onPcHoverEnter = this._onPcHoverEnter.bind(this);
    this._onPcHoverLeave = this._onPcHoverLeave.bind(this);
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onTabScroll = this._onTabScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    /** @type {ResizeObserver | null} */
    this._tabOverflowObserver = null;
    this._tickProgress = this._tickProgress.bind(this);
    this._onSwipeStart = this._onSwipeStart.bind(this);
    this._onSwipeMove = this._onSwipeMove.bind(this);
    this._onSwipeEnd = this._onSwipeEnd.bind(this);
  }

  connectedCallback() {
    this._unbind();

    this.autoEnabled = this.dataset.enableAutoPlay === 'true';
    this.autoDuration = Math.max(2, Number.parseInt(this.dataset.autoPlayDuration || '10', 10)) * 1000;
    this.loop = this.dataset.loop === 'true';

    this.slides = Array.from(this.querySelectorAll('[data-bg-progress-v2-slide]'));
    this.media =
      this.querySelector('[data-bg-progress-v2-media]') ||
      this.querySelector('.bg-progress-carousel-v2__media');
    this.tabRow = this.querySelector('[data-bg-progress-v2-tabs]');
    this.stage = this.querySelector('.bg-progress-carousel-v2__stage');
    this.prevButton = this.querySelector('[data-bg-progress-v2-prev]');
    this.nextButton = this.querySelector('[data-bg-progress-v2-next]');

    this.slideCount = this.slides.length;
    if (this.slideCount < 1) return;

    this._buildTabs();
    this._syncChromeVisibility();
    this._syncSlideVisibility(0);
    this.classList.add('is-ready');

    this.tabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));
    this.stage?.addEventListener('mouseenter', this._onPcHoverEnter);
    this.stage?.addEventListener('mouseleave', this._onPcHoverLeave);
    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);
    this.tabRow?.addEventListener('scroll', this._onTabScroll);
    window.addEventListener('resize', this._onResize);
    this._bindTabOverflowObserver();
    this._bindSwipeTargets();

    if (this.autoEnabled && this.slideCount > 1) {
      this._setupObserver();
    }

    this.setActive(0, false);

    requestAnimationFrame(() => {
      this._updateMoArrows();
      requestAnimationFrame(() => {
        this.classList.add('is-animated');
      });
    });
  }

  disconnectedCallback() {
    this._unbind();
  }

  _unbind() {
    this._stopProgress();
    this._teardownObserver();
    this._unbindSwipeTargets();
    this._unbindTabOverflowObserver();
    this.tabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
    this.stage?.removeEventListener('mouseenter', this._onPcHoverEnter);
    this.stage?.removeEventListener('mouseleave', this._onPcHoverLeave);
    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    this.tabRow?.removeEventListener('scroll', this._onTabScroll);
    window.removeEventListener('resize', this._onResize);
  }

  /**
   * Build progress tabs from rendered slides.
   * Required because Liquid section.blocks is unreliable for theme blocks.
   */
  _buildTabs() {
    if (!this.tabRow || this.slideCount < 2) {
      this.tabs = [];
      if (this.tabRow) this.tabRow.replaceChildren();
      return;
    }

    this.tabRow.replaceChildren();

    this.slides.forEach((slide, index) => {
      const tabLabel = slide.dataset.bgProgressV2Label || '';
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'bg-progress-carousel-v2__tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tab.dataset.bgProgressV2Tab = String(index);

      const track = document.createElement('span');
      track.className = 'bg-progress-carousel-v2__track';
      track.setAttribute('aria-hidden', 'true');

      const fill = document.createElement('span');
      fill.className = 'bg-progress-carousel-v2__fill';
      fill.dataset.bgProgressV2Fill = '';
      track.appendChild(fill);

      const label = document.createElement('span');
      label.className = 'bg-progress-carousel-v2__label font-base-medium';
      label.textContent = tabLabel;

      tab.appendChild(track);
      tab.appendChild(label);
      this.tabRow.appendChild(tab);
    });

    this.tabs = Array.from(this.tabRow.querySelectorAll('[data-bg-progress-v2-tab]'));
  }

  _syncChromeVisibility() {
    const hasMultiple = this.slideCount > 1;

    if (this.tabRow) this.tabRow.hidden = !hasMultiple;
  }

  /**
   * @returns {boolean}
   */
  _isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /**
   * @returns {boolean}
   */
  _isPc() {
    return window.matchMedia('(min-width: 1280px)').matches;
  }

  _bindTabOverflowObserver() {
    this._unbindTabOverflowObserver();
    if (!this.tabRow || typeof ResizeObserver === 'undefined') return;

    this._tabOverflowObserver = new ResizeObserver(() => {
      this._updateMoArrows();
    });
    this._tabOverflowObserver.observe(this.tabRow);
    const tabBar = this.tabRow.parentElement;
    if (tabBar && tabBar !== this.tabRow) {
      this._tabOverflowObserver.observe(tabBar);
    }
  }

  _unbindTabOverflowObserver() {
    if (!this._tabOverflowObserver) return;
    this._tabOverflowObserver.disconnect();
    this._tabOverflowObserver = null;
  }

  _bindSwipeTargets() {
    this._unbindSwipeTargets();
    this._swipeTargets = [this.media, this.tabRow].filter(Boolean);

    this._swipeTargets.forEach((target) => {
      target.addEventListener('touchstart', this._onSwipeStart, { passive: true });
      target.addEventListener('touchmove', this._onSwipeMove, { passive: false });
      target.addEventListener('touchend', this._onSwipeEnd, { passive: true });
      target.addEventListener('touchcancel', this._onSwipeEnd, { passive: true });
    });
  }

  _unbindSwipeTargets() {
    this._swipeTargets.forEach((target) => {
      target.removeEventListener('touchstart', this._onSwipeStart);
      target.removeEventListener('touchmove', this._onSwipeMove);
      target.removeEventListener('touchend', this._onSwipeEnd);
      target.removeEventListener('touchcancel', this._onSwipeEnd);
    });
    this._swipeTargets = [];
    this._swipeState = null;
  }

  /**
   * @param {TouchEvent} event
   */
  _onSwipeStart(event) {
    if (!this._isMobile() || this.slideCount < 2) return;
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this._swipeState = {
      startX: touch.clientX,
      startY: touch.clientY,
      tracking: true,
      axis: null,
      moved: false,
    };
  }

  /**
   * @param {TouchEvent} event
   */
  _onSwipeMove(event) {
    const state = this._swipeState;
    if (!state?.tracking || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;

    if (state.axis == null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      state.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    if (state.axis === 'x') {
      state.moved = true;
      if (event.cancelable) event.preventDefault();
    }
  }

  /**
   * @param {TouchEvent} event
   */
  _onSwipeEnd(event) {
    const state = this._swipeState;
    this._swipeState = null;
    if (!state?.tracking || state.axis !== 'x' || !state.moved) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - state.startX;
    if (Math.abs(dx) < 40) return;

    this._suppressTabClick = true;
    window.setTimeout(() => {
      this._suppressTabClick = false;
    }, 0);

    this._stepBy(dx < 0 ? 1 : -1);
  }

  /**
   * @param {number} delta
   */
  _stepBy(delta) {
    if (this.slideCount < 2 || delta === 0) return;

    let nextIndex = this.activeIndex + delta;
    if (nextIndex < 0) {
      nextIndex = this.loop ? this.slideCount - 1 : 0;
    } else if (nextIndex >= this.slideCount) {
      nextIndex = this.loop ? 0 : this.slideCount - 1;
    }

    if (nextIndex === this.activeIndex) return;
    this.setActive(nextIndex, true);
  }

  _setupObserver() {
    if (this.intersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.isInView = entry.isIntersecting;
          if (entry.isIntersecting) {
            this._startProgress();
          } else {
            this._stopProgress();
          }
        }
      },
      { threshold: 0.1 }
    );

    this.intersectionObserver.observe(this);
  }

  _teardownObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  _startProgress() {
    if (!this.autoEnabled || this.slideCount < 2 || this.paused || !this.isInView) return;
    if (this.progressRaf != null) return;

    this.progressStart = performance.now();
    this.progressRaf = requestAnimationFrame(this._tickProgress);
  }

  _stopProgress() {
    if (this.progressRaf != null) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
  }

  /**
   * @returns {number}
   */
  _currentElapsed() {
    if (this.progressRaf == null) return this.elapsedBeforePause;
    return Math.min(this.elapsedBeforePause + (performance.now() - this.progressStart), this.autoDuration);
  }

  _tickProgress() {
    const elapsed = this.elapsedBeforePause + (performance.now() - this.progressStart);
    const ratio = Math.min(elapsed / this.autoDuration, 1);
    this._setFill(this.activeIndex, ratio);

    if (ratio >= 1) {
      this.progressRaf = null;
      this.elapsedBeforePause = 0;
      this._goToNext();
      return;
    }

    this.progressRaf = requestAnimationFrame(this._tickProgress);
  }

  _goToNext() {
    if (this.slideCount < 2) return;

    let nextIndex = this.activeIndex + 1;
    if (nextIndex >= this.slideCount) {
      if (!this.loop) return;
      nextIndex = 0;
    }

    this.setActive(nextIndex, false);
  }

  /**
   * @param {Event} event
   */
  _onTabClick(event) {
    if (this._suppressTabClick) {
      event.preventDefault();
      return;
    }

    const tab = event.currentTarget;
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(tab.dataset.bgProgressV2Tab || '', 10);
    if (Number.isNaN(index)) return;

    this.setActive(index, true);
  }

  _onPcHoverEnter() {
    if (!this._isPc()) return;
    this._pauseProgress();
  }

  _onPcHoverLeave() {
    if (!this._isPc()) return;
    this._resumeProgress();
  }

  /**
   * Same pause path as the former pause button — keep elapsed, freeze fill.
   */
  _pauseProgress() {
    if (!this.autoEnabled || this.slideCount < 2 || this.paused) return;

    this.elapsedBeforePause = this._currentElapsed();
    this.paused = true;
    this._stopProgress();
    this._setFill(this.activeIndex, this.elapsedBeforePause / this.autoDuration);
  }

  /**
   * Same resume path as the former play button — continue from elapsed.
   */
  _resumeProgress() {
    if (!this.autoEnabled || this.slideCount < 2 || !this.paused) return;

    this.paused = false;
    this._startProgress();
  }

  _onPrevClick() {
    this._stepBy(-1);
  }

  _onNextClick() {
    this._stepBy(1);
  }

  _onTabScroll() {
    this._updateMoArrows();
  }

  _onResize() {
    this._updateMoArrows();
    this._scrollTabIntoView(this.activeIndex, false);

    if (!this._isPc()) {
      this._resumeProgress();
    } else if (this.stage?.matches(':hover')) {
      this._pauseProgress();
    }
  }

  /**
   * @param {number} index
   * @param {number} ratio
   */
  _setFill(index, ratio) {
    const fill = this.tabs[index]?.querySelector('[data-bg-progress-v2-fill]');
    if (!fill) return;

    const percent = `${Math.max(0, Math.min(ratio, 1)) * 100}%`;
    fill.style.setProperty('--bg-progress-v2-fill', percent);
  }

  _resetFillsExceptCurrent() {
    this.tabs.forEach((_, index) => {
      if (index === this.activeIndex) return;
      this._setFill(index, 0);
    });
  }

  /**
   * Center the active tab in the tab scroller (clamped at the ends).
   * Avoid Element.scrollIntoView — it also scrolls the document.
   *
   * @param {number} index
   * @param {boolean} animate
   */
  _scrollTabIntoView(index, animate) {
    const tab = this.tabs[index];
    if (!tab || !this.tabRow) return;

    const tabRow = this.tabRow;
    const maxScroll = Math.max(0, tabRow.scrollWidth - tabRow.clientWidth);
    if (maxScroll <= 0) return;

    const tabRect = tab.getBoundingClientRect();
    const rowRect = tabRow.getBoundingClientRect();
    const tabLeft = tabRow.scrollLeft + (tabRect.left - rowRect.left);
    const tabCenter = tabLeft + tabRect.width / 2;
    const nextScrollLeft = Math.max(0, Math.min(maxScroll, tabCenter - tabRow.clientWidth / 2));

    if (Math.abs(nextScrollLeft - tabRow.scrollLeft) < 1) return;

    tabRow.scrollTo({
      left: nextScrollLeft,
      behavior: animate ? 'smooth' : 'auto',
    });
  }

  /**
   * Whether tab labels exceed the middle column (arrow slots already reserved
   * as image-aligned inset). 1px slack avoids subpixel false positives.
   *
   * @returns {boolean}
   */
  _tabsOverflow() {
    const tabRow = this.tabRow;
    if (!tabRow || tabRow.hidden) return false;

    return tabRow.scrollWidth > tabRow.clientWidth + 1;
  }

  /**
   * MO arrows: hidden unless mobile, more than one slide, the tab row overflows,
   * and that direction can still advance. Side columns stay reserved so tabs
   * keep aligning with the image whether arrows are visible or not.
   */
  _updateMoArrows() {
    if (!this.prevButton || !this.nextButton) return;

    const overflowing = this._isMobile() && this.slideCount >= 2 && this._tabsOverflow();

    if (!overflowing) {
      this.prevButton.hidden = true;
      this.nextButton.hidden = true;
      return;
    }

    if (this.loop) {
      this.prevButton.hidden = false;
      this.nextButton.hidden = false;
      return;
    }

    this.prevButton.hidden = this.activeIndex <= 0;
    this.nextButton.hidden = this.activeIndex >= this.slideCount - 1;
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  _syncOverlayTheme(slide) {
    this.classList.toggle('is-slide-light', Boolean(slide?.classList.contains('component-light')));
    this.classList.toggle('is-slide-dark', Boolean(slide?.classList.contains('component-dark')));
  }

  /**
   * Media stack host for a slide (theme-block wrapper when present).
   * @param {HTMLElement | undefined} slide
   * @returns {HTMLElement | null}
   */
  _slideHost(slide) {
    if (!slide) return null;
    const wrapper = slide.closest('.shopify-block__bg-progress-carousel-v2-slide');
    return wrapper || slide;
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  _clearCopyAnimationStyles(slide) {
    const copy = slide?.querySelector('.bg-progress-carousel-v2-slide__copy');
    if (!copy) return;

    copy.getAnimations().forEach((animation) => animation.cancel());
    copy.style.removeProperty('opacity');
  }

  /**
   * @returns {boolean}
   */
  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * slideshow-v2 `content_fade` host crossfade, then copy fades in as one block
   * (opacity only — no translateY stagger).
   *
   * @param {HTMLElement} fromHost
   * @param {HTMLElement} toHost
   * @param {HTMLElement} toSlide
   * @returns {Promise<void>}
   */
  async _contentFade(fromHost, toHost, toSlide) {
    const reduced = this._prefersReducedMotion();
    const fadeMs = reduced ? 0 : 300;
    const contentMs = reduced ? 0 : 400;
    const contentEase = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const contentAtMs = reduced ? 0 : 400;

    fromHost.getAnimations().forEach((animation) => animation.cancel());
    toHost.getAnimations().forEach((animation) => animation.cancel());
    this._clearCopyAnimationStyles(toSlide);

    const toCopy = toSlide.querySelector('.bg-progress-carousel-v2-slide__copy');
    if (toCopy) toCopy.style.opacity = '0';

    fromHost.style.zIndex = '1';
    toHost.style.zIndex = '0';
    toHost.style.visibility = 'visible';
    toHost.style.opacity = '0';

    /** @type {Animation[]} */
    const animations = [];

    if (fadeMs === 0) {
      fromHost.style.opacity = '0';
      fromHost.style.visibility = 'hidden';
      fromHost.style.zIndex = '0';
      toHost.style.opacity = '1';
      toHost.style.visibility = 'visible';
      toHost.style.zIndex = '1';
    } else {
      animations.push(
        fromHost.animate(
          [
            { opacity: 1, visibility: 'visible' },
            { opacity: 0, visibility: 'hidden' },
          ],
          { duration: fadeMs, easing: 'ease-in', fill: 'forwards' }
        ),
        toHost.animate(
          [
            { opacity: 0, visibility: 'hidden' },
            { opacity: 1, visibility: 'visible' },
          ],
          { duration: fadeMs, easing: 'ease-out', fill: 'forwards' }
        )
      );
    }

    if (toCopy && contentMs > 0) {
      animations.push(
        toCopy.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: contentMs,
          delay: contentAtMs,
          easing: contentEase,
          fill: 'forwards',
        })
      );
    } else if (toCopy) {
      toCopy.style.opacity = '1';
    }

    if (animations.length > 0) {
      await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
    }

    fromHost.style.opacity = '0';
    fromHost.style.visibility = 'hidden';
    fromHost.style.zIndex = '0';
    toHost.style.opacity = '1';
    toHost.style.visibility = 'visible';
    toHost.style.zIndex = '1';

    animations.forEach((animation) => animation.cancel());
    this._clearCopyAnimationStyles(toSlide);
  }

  /**
   * Clear fade leftovers and sync is-active on slide + host.
   *
   * @param {number} index
   */
  _syncSlideVisibility(index) {
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      const host = this._slideHost(slide);

      slide.classList.toggle('is-active', isActive);
      slide.classList.remove('is-leaving');
      slide.style.removeProperty('opacity');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('z-index');
      this._clearCopyAnimationStyles(slide);

      if (host && host !== slide) {
        host.classList.toggle('is-active', isActive);
        host.classList.remove('is-leaving');
        host.style.removeProperty('opacity');
        host.style.removeProperty('visibility');
        host.style.removeProperty('z-index');
      }
    });
  }

  /**
   * @param {number} fromIndex
   * @param {number} toIndex
   * @returns {Promise<void>}
   */
  async _transitionSlides(fromIndex, toIndex) {
    const fromSlide = this.slides[fromIndex];
    const toSlide = this.slides[toIndex];
    const fromHost = this._slideHost(fromSlide);
    const toHost = this._slideHost(toSlide);
    if (!fromHost || !toHost || fromHost === toHost) {
      this._syncSlideVisibility(toIndex);
      return;
    }

    const token = (this._transitionToken = (this._transitionToken || 0) + 1);

    fromHost.classList.add('is-leaving');
    fromHost.classList.remove('is-active');
    fromSlide.classList.add('is-leaving');
    fromSlide.classList.remove('is-active');

    toHost.classList.add('is-active');
    toSlide.classList.add('is-active');

    await this._contentFade(fromHost, toHost, toSlide);

    if (token !== this._transitionToken) return;

    fromHost.classList.remove('is-leaving');
    fromSlide.classList.remove('is-leaving');
    this._syncSlideVisibility(toIndex);
  }

  /**
   * @param {number} index
   * @param {boolean} fromUser
   */
  setActive(index, fromUser) {
    if (index < 0 || index >= this.slideCount) return;

    const previousIndex = this.activeIndex;
    const tabChanged = fromUser && index !== previousIndex;
    const shouldAnimate = this.classList.contains('is-animated') && previousIndex !== index;

    this.activeIndex = index;

    if (shouldAnimate) {
      this._transitionSlides(previousIndex, index);
    } else {
      this._syncSlideVisibility(index);
    }

    this.tabs.forEach((tab, tabIndex) => {
      tab.setAttribute('aria-selected', tabIndex === index ? 'true' : 'false');
    });

    this._syncOverlayTheme(this.slides[index]);

    if (!(fromUser && this.paused && !tabChanged)) {
      this.elapsedBeforePause = 0;
      this._resetFillsExceptCurrent();
      this._setFill(index, !fromUser && !this.autoEnabled ? 1 : 0);
    }

    this._stopProgress();
    this._startProgress();
    this._scrollTabIntoView(index, fromUser);
    this._updateMoArrows();
  }
}

if (!customElements.get('bg-progress-carousel-v2')) {
  customElements.define('bg-progress-carousel-v2', BgProgressCarouselV2);
}
